import { IBaseModel } from '../system/BaseModel';
import { SubscriptionTier } from '../Billing/Subscription';
import { IFingotiDevice } from '../FingotiDevice/FingotiDevice';

export interface IPlanetDeviceUpdateFormFields extends Omit<
  IPlanetDeviceUpdateRequest,
  'serialSettings' | 'powerSettings' | 'peripheralSettings'
> {
  serialSettings: {
    useDefault: boolean;
    baudRate: string;
    dataBits: string;
    parity: string;
    stopBits: string;
  };
  powerSettings: {
    measuredVoltageOne: string;
    measuredVoltageTwo: string;
    measuredVoltageThree: string;
    phase: string;
    frequency: string;
  };
  peripheralSettings: {
    ctClamp: {
      enabled: boolean;
      offset: string;
      scale: string;
      current: string;
      resolution: string;
    };
    voltmeter: {
      enabled: boolean;
      offset: string;
      scale: string;
    };
    temperatureSensor: {
      enabled: boolean;
      offset: string;
      scale: string;
      location: string;
    };
  };
}

export interface IPlanetDeviceUpdateRequest extends Omit<
  IPlanetDevice,
  'organisationId' | 'protocol'
> {}

export interface IPlanetDevice extends IBaseModel {
  organisationId: string;
  serialNumber: string;
  partNumber: string;
  billing: IPlanetDeviceBilling;
  serialSettings: IPlanetDeviceSerialSettings;
  peripheralSettings: IPlanetDevicePeripheralSettings;
  powerSettings: IPlanetDevicePowerSettings;
  protocol: PlanetDeviceProtocol;
  setupRequired: boolean;
}

export interface IPlanetDeviceWithFingotiDevice extends IPlanetDevice {
  gateway: IFingotiDevice;
}

export interface IPlanetDeviceBilling {
  subscriptionId?: string;
  tier: SubscriptionTier;
  managedBy?: string;
}

export interface IPlanetDeviceSerialSettings {
  useDefault: boolean;
  baudRate: number;
  dataBits: number;
  parity: 0 | 1 | 2;
  stopBits: number;
}

export interface IPlanetDevicePeripheralSettings {
  ctClamp: IPlanetDeviceCTClampConfig;
  voltmeter: IPlanetDeviceVoltMeterConfig;
  temperatureSensor: IPlanetDeviceTemperatureSensorConfig;
}

export interface IPlanetDevicePeripheral {
  enabled: boolean;
  offset: number;
  scale: number;
}

export interface IPlanetDevicePowerSettings {
  phase: PlanetDevicePowerPhase;
  frequency: number;
  measuredVoltageOne?: number;
  measuredVoltageTwo?: number;
  measuredVoltageThree?: number;
}

export interface IPlanetDeviceVoltMeterConfig extends IPlanetDevicePeripheral {}

export interface IPlanetDeviceCTClampConfig extends IPlanetDevicePeripheral {
  current: number;
  resolution: number;
}

export interface IPlanetDeviceTemperatureSensorConfig extends IPlanetDevicePeripheral {
  location: PlanetDeviceTemperatureProbeLocation;
}

export enum PlanetDevicePowerPhase {
  SinglePhase = 1,
  ThreePhase = 3,
}

export enum PlanetDeviceTemperatureProbeLocation {
  Outdoor,
  Indoor,
}

export enum PlanetDeviceProtocol {
  Modbus,
  Bacnet,
  Opentherm,
  Mbus,
  Ebus,
  Canbus,
}

export const BaudRateOptions = [
  1200, 2400, 4800, 9600, 14400, 19200, 28800, 38400, 56000, 57600, 115200,
];

export const DataBitsOptions = [5, 6, 7, 8];

export const ParityOptions = [
  { key: 'None', value: 0 },
  { key: 'Odd', value: 1 },
  { key: 'Even', value: 2 },
];

export const StopBitsOptions = [1, 2];

export const PartNumberModel = {
  '386363-249': 'Atmo',
  '613302-637': 'AtmoPro',
};
